import React from 'react';
import { ArrowRight, Scissors, ShieldCheck, CheckCircle, BookOpen, ChevronRight } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

interface HeroProps {
  onStartClick: () => void;
  onGuidesClick?: () => void;
}

export const Hero: React.FC<HeroProps> = ({ onStartClick, onGuidesClick }) => {
  const { language, t } = useLanguage();

  return (
    <div className="max-w-4xl mx-auto px-4 text-center">
      
      {/* 1. HERO MAIN PRESENTATION */}
      <section className="pt-16 pb-20 sm:pt-28 sm:pb-32 lg:pt-36 lg:pb-40 space-y-8 sm:space-y-12">
        {/* Logo + Beta Pill */}
        <div className="flex flex-col items-center gap-4">
          <img
            src="/vidly-logo.png"
            alt="Vidly AI"
            className="w-16 h-16 sm:w-20 sm:h-20 object-contain invert brightness-125"
          />
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full border border-zinc-800 bg-zinc-900/50 text-zinc-300 text-xs font-medium max-w-full">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse shrink-0"></span>
            <span className="truncate">
              {language === 'en'
                ? 'Public Beta: Up to 100 videos • Sign in with Google'
                : 'Beta Abierta: Limitada a 100 vídeos • Acceso con Google'}
            </span>
          </div>
        </div>

        {/* Main Headline & Subtitle */}
        <div className="space-y-6 sm:space-y-8 my-6 sm:my-10 max-w-3xl mx-auto">
          <h1 className="text-4xl sm:text-6xl md:text-7xl lg:text-8xl font-black tracking-[-0.035em] text-white leading-[1.05] sm:leading-[0.98]">
            {language === 'en' ? (
              <>AI Video Summarizer — Summarize long videos without quality loss</>
            ) : (
              <>Resume vídeos largos con Inteligencia Artificial sin perder calidad</>
            )}
          </h1>
          <p className="text-base sm:text-lg md:text-xl text-zinc-400 max-w-2xl mx-auto font-normal leading-relaxed pt-2">
            {language === 'en'
              ? 'Smart AI video summarizer: tell the AI what to keep and our engine plans and cuts frame-accurate highlights directly from your footage in seconds.'
              : 'La herramienta inteligente para resumir vídeos largos, podcasts y clases con IA. Indica qué conservar y nuestro motor genera cortes limpios fotograma a fotograma sin recomprimir.'}
          </p>
        </div>

        {/* Call to action */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-4 sm:pt-6 w-full max-w-xs sm:max-w-none mx-auto">
          <button
            onClick={onStartClick}
            className="btn-primary w-full sm:w-auto px-8 py-3.5 text-base font-semibold shadow-sm flex items-center justify-center gap-2"
          >
            <span>{t.hero.ctaPrimary}</span>
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </section>

      {/* 2. MINIMALIST FEATURES */}
      <section className="reveal-on-scroll py-12 sm:py-20 max-w-3xl mx-auto">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 sm:gap-7 text-left">
          <div className="p-6 sm:p-7 rounded-2xl border border-zinc-800/80 bg-zinc-900/40 space-y-2.5">
            <div className="flex items-center gap-2.5 text-zinc-100 font-semibold text-sm">
              <Scissors className="w-4 h-4 text-zinc-400 shrink-0" />
              <span>{language === 'en' ? 'Lossless Cuts' : 'Cortes reales FFmpeg'}</span>
            </div>
            <p className="text-zinc-400 text-sm leading-relaxed font-normal">
              {language === 'en'
                ? 'Native video footage without generative AI hallucinations or pixel distortion.'
                : 'Metraje nativo limpio sin alucinaciones de vídeo generativo.'}
            </p>
          </div>

          <div className="p-6 sm:p-7 rounded-2xl border border-zinc-800/80 bg-zinc-900/40 space-y-2.5">
            <div className="flex items-center gap-2.5 text-zinc-100 font-semibold text-sm">
              <CheckCircle className="w-4 h-4 text-zinc-400 shrink-0" />
              <span>{language === 'en' ? 'Mandatory Clips' : 'Intervalos obligatorios'}</span>
            </div>
            <p className="text-zinc-400 text-sm leading-relaxed font-normal">
              {language === 'en'
                ? 'Pin key scenes and explanations that the AI must never cut.'
                : 'Fija partes clave que la IA nunca debe recortar.'}
            </p>
          </div>

          <div className="p-6 sm:p-7 rounded-2xl border border-zinc-800/80 bg-zinc-900/40 space-y-2.5">
            <div className="flex items-center gap-2.5 text-zinc-100 font-semibold text-sm">
              <ShieldCheck className="w-4 h-4 text-zinc-400 shrink-0" />
              <span>{language === 'en' ? 'Privacy & GDPR' : 'Privacidad & RGPD'}</span>
            </div>
            <p className="text-zinc-400 text-sm leading-relaxed font-normal">
              {language === 'en'
                ? 'Client-side processing and automatic 24-hour purge. No training on user data.'
                : 'Purga automática en 24h y modelos privados sin entrenamiento.'}
            </p>
          </div>
        </div>
      </section>

      {/* 3. DEEP EXPLANATORY ARCHITECTURE SECTION */}
      <section className="reveal-on-scroll pt-24 sm:pt-36 pb-16 sm:pb-24 text-left max-w-3xl mx-auto border-t border-zinc-800/60 mt-12 sm:mt-20 space-y-16 sm:space-y-20">
        <div className="space-y-4 text-center max-w-2xl mx-auto">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight text-white">
            {language === 'en'
              ? 'How does the Vidly AI trimming architecture work?'
              : '¿Cómo funciona la arquitectura de corte de Vidly AI?'}
          </h2>
          <p className="text-sm sm:text-base text-zinc-400 max-w-xl mx-auto leading-relaxed font-normal pt-1">
            {language === 'en'
              ? 'We combine natural language understanding with precision media engines to trim videos cleanly without quality loss.'
              : 'Combinamos modelos de lenguaje semántico para entender el discurso con motores multimedia C++ para recortar sin recomprimir.'}
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 sm:gap-7">
          <div className="p-7 sm:p-8 rounded-2xl bg-zinc-900/40 border border-zinc-800 space-y-3">
            <span className="text-xs font-bold text-zinc-500 tracking-wider uppercase">
              {language === 'en' ? 'STEP 1' : 'PASO 1'}
            </span>
            <h3 className="text-base font-bold text-white tracking-tight">
              {language === 'en' ? 'Audio & Speech Extraction' : 'Extracción y Detección Acústica'}
            </h3>
            <p className="text-sm text-zinc-300/90 leading-relaxed font-normal">
              {language === 'en'
                ? 'Voice activity algorithms analyze speech to isolate full sentences and natural breathing pauses.'
                : 'Analizamos la pista de audio con algoritmos VAD para aislar oraciones enteras y silencios respiratorios naturales.'}
            </p>
          </div>

          <div className="p-7 sm:p-8 rounded-2xl bg-zinc-900/40 border border-zinc-800 space-y-3">
            <span className="text-xs font-bold text-zinc-500 tracking-wider uppercase">
              {language === 'en' ? 'STEP 2' : 'PASO 2'}
            </span>
            <h3 className="text-base font-bold text-white tracking-tight">
              {language === 'en' ? 'AI Editorial Planning' : 'Planificación Editorial IA'}
            </h3>
            <p className="text-sm text-zinc-300/90 leading-relaxed font-normal">
              {language === 'en'
                ? 'The AI interprets instructions, preserves mandatory intervals, and selects the strongest moments.'
                : 'La IA interpreta tus instrucciones, respeta tus fragmentos obligatorios y elige las mejores ideas.'}
            </p>
          </div>

          <div className="p-7 sm:p-8 rounded-2xl bg-zinc-900/40 border border-zinc-800 space-y-3">
            <span className="text-xs font-bold text-zinc-500 tracking-wider uppercase">
              {language === 'en' ? 'STEP 3' : 'PASO 3'}
            </span>
            <h3 className="text-base font-bold text-white tracking-tight">
              {language === 'en' ? 'Clean Cut & Assembly' : 'Corte Limpio con Micro-fades'}
            </h3>
            <p className="text-sm text-zinc-300/90 leading-relaxed font-normal">
              {language === 'en'
                ? 'Direct cuts on keyframes with subtle crossfading deliver a polished MP4 ready to download.'
                : 'Ejecuta cortes directos en los fotogramas clave y funde el audio con crossfade suave.'}
            </p>
          </div>
        </div>

        {/* 4. USE CASES GRID */}
        <div className="reveal-on-scroll p-8 sm:p-12 rounded-3xl bg-zinc-900/20 border border-zinc-800/80 space-y-8 mt-16 sm:mt-24">
          <div className="space-y-2">
            <h3 className="text-lg sm:text-xl font-bold text-white tracking-tight">
              {language === 'en' ? 'Built for professional workflows' : 'Diseñado para flujos de trabajo profesionales'}
            </h3>
            <p className="text-sm text-zinc-400">
              {language === 'en'
                ? 'Tailored for content creators, educators, and product teams.'
                : 'Adaptado a las exigencias reales de creadores, educadores y equipos de producto.'}
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 sm:gap-10 pt-2 text-sm">
            <div className="space-y-2">
              <span className="font-semibold text-zinc-100 text-sm sm:text-base block">
                🎙️ {language === 'en' ? 'Podcasters & Creators' : 'Podcasters & Creadores'}
              </span>
              <p className="text-zinc-400 leading-relaxed font-normal">
                {language === 'en'
                  ? 'Turn 1-hour interviews into punchy 5-minute highlight recaps preserving the best insights.'
                  : 'Convierte entrevistas de 1 hora en resúmenes ágiles de 5 minutos conservando las mejores reflexiones.'}
              </p>
            </div>
            <div className="space-y-2">
              <span className="font-semibold text-zinc-100 text-sm sm:text-base block">
                🎓 {language === 'en' ? 'Lectures & Webinars' : 'Clases y Webinars Educativos'}
              </span>
              <p className="text-zinc-400 leading-relaxed font-normal">
                {language === 'en'
                  ? 'Remove connection dead time, chit-chat, and extract key theoretical takeaways.'
                  : 'Elimina pausas de conexión, tiempos muertos y extrae las conclusiones teóricas clave.'}
              </p>
            </div>
            <div className="space-y-2">
              <span className="font-semibold text-zinc-100 text-sm sm:text-base block">
                💼 {language === 'en' ? 'Product Demos & Meetings' : 'Demos de Producto y Reuniones'}
              </span>
              <p className="text-zinc-400 leading-relaxed font-normal">
                {language === 'en'
                  ? 'Synthesize Zoom calls and software walkthroughs highlighting only demonstrated features.'
                  : 'Sintetiza llamadas de Zoom o presentaciones de software destacando solo las funcionalidades mostradas.'}
              </p>
            </div>
            <div className="space-y-2">
              <span className="font-semibold text-zinc-100 text-sm sm:text-base block">
                ⚡ {language === 'en' ? 'Marketing Agencies' : 'Agencias de Marketing'}
              </span>
              <p className="text-zinc-400 leading-relaxed font-normal">
                {language === 'en'
                  ? 'Produce social media video variations at a fraction of manual editing time.'
                  : 'Genera variantes de vídeo para anuncios y redes sociales a una fracción del tiempo de edición manual.'}
              </p>
            </div>
          </div>
        </div>

        {/* 5. EDITORIAL GUIDES & TECHNICAL ARTICLES PREVIEW */}
        {onGuidesClick && (
          <div className="reveal-on-scroll p-8 sm:p-10 rounded-3xl bg-zinc-900/30 border border-zinc-800/80 space-y-6 mt-16 sm:mt-24">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-zinc-800/80 pb-5">
              <div className="space-y-1">
                <div className="flex items-center gap-2 text-white font-bold text-lg sm:text-xl">
                  <BookOpen className="w-5 h-5 text-zinc-400" />
                  <span>{language === 'en' ? 'Knowledge Hub & Technical Guides' : 'Centro de Recursos y Guías Editoriales'}</span>
                </div>
                <p className="text-xs sm:text-sm text-zinc-400">
                  {language === 'en'
                    ? 'Explore technical documentation on FFmpeg trimming, VAD acoustics, and video engineering.'
                    : 'Artículos en profundidad sobre edición con FFmpeg, optimización de códecs e inteligencia artificial.'}
                </p>
              </div>

              <button
                onClick={onGuidesClick}
                className="btn-secondary text-xs py-2 px-3.5 flex items-center gap-1.5 shrink-0 self-start sm:self-auto"
              >
                <span>{language === 'en' ? 'Browse all 6 guides' : 'Ver las 6 guías'}</span>
                <ChevronRight className="w-3.5 h-3.5" />
              </button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-left">
              <div
                onClick={onGuidesClick}
                className="p-4 rounded-xl bg-zinc-950/60 border border-zinc-800/80 hover:border-zinc-700 transition-colors cursor-pointer group space-y-2"
              >
                <span className="text-[10px] font-semibold text-zinc-400 uppercase tracking-wider block">
                  {language === 'en' ? 'Engineering' : 'Ingeniería FFmpeg'}
                </span>
                <h4 className="text-xs sm:text-sm font-semibold text-white group-hover:text-zinc-200 transition-colors line-clamp-2">
                  {language === 'en'
                    ? 'Surgical Trimming vs Generative AI: Why Native Footage Matters'
                    : 'Edición Quirúrgica con FFmpeg vs Vídeo Generativo'}
                </h4>
                <p className="text-[11px] text-zinc-400 line-clamp-2">
                  {language === 'en'
                    ? 'Why AI tools often distort frames and how native stream copy preserves 100% of camera fidelity.'
                    : 'Por qué la recodificación destruye el bitrate y cómo el corte nativo preserva la fidelidad.'}
                </p>
              </div>

              <div
                onClick={onGuidesClick}
                className="p-4 rounded-xl bg-zinc-950/60 border border-zinc-800/80 hover:border-zinc-700 transition-colors cursor-pointer group space-y-2"
              >
                <span className="text-[10px] font-semibold text-zinc-400 uppercase tracking-wider block">
                  {language === 'en' ? 'Content Strategy' : 'Estrategia de Audio'}
                </span>
                <h4 className="text-xs sm:text-sm font-semibold text-white group-hover:text-zinc-200 transition-colors line-clamp-2">
                  {language === 'en'
                    ? 'Summarizing Podcasts Without Chopping Sentences in Half'
                    : 'Cómo resumir podcasts sin cortar oraciones a la mitad'}
                </h4>
                <p className="text-[11px] text-zinc-400 line-clamp-2">
                  {language === 'en'
                    ? 'Voice Activity Detection (VAD) and natural cadence snapping explained.'
                    : 'Detección acústica VAD y transiciones suaves de audio con micro-fades.'}
                </p>
              </div>

              <div
                onClick={onGuidesClick}
                className="p-4 rounded-xl bg-zinc-950/60 border border-zinc-800/80 hover:border-zinc-700 transition-colors cursor-pointer group space-y-2"
              >
                <span className="text-[10px] font-semibold text-zinc-400 uppercase tracking-wider block">
                  {language === 'en' ? 'Workflow' : 'Flujo de Trabajo'}
                </span>
                <h4 className="text-xs sm:text-sm font-semibold text-white group-hover:text-zinc-200 transition-colors line-clamp-2">
                  {language === 'en'
                    ? 'Mandatory Ranges: Protecting Crucial Moments in AI Edits'
                    : 'Intervalos Obligatorios: Protege escenas clave en la IA'}
                </h4>
                <p className="text-[11px] text-zinc-400 line-clamp-2">
                  {language === 'en'
                    ? 'Pin essential clips that the AI is mathematically prohibited from deleting.'
                    : 'Fija escenas sagradas y primicias para que la IA nunca las elimine.'}
                </p>
              </div>
            </div>
          </div>
        )}
      </section>

    </div>
  );
};
